import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Copy, Check, Loader2, Key } from 'lucide-react';
import { User } from '@/types/user';

interface GeneratePasswordResetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
}

export function GeneratePasswordResetDialog({
  open,
  onOpenChange,
  user,
}: GeneratePasswordResetDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const generateResetLink = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        'admin-generate-password-reset',
        {
          body: { userId: user.id },
        }
      );

      if (error) throw error;

      if (data?.resetLink) {
        setResetLink(data.resetLink);
        toast({
          title: 'Success',
          description: 'Password reset link generated successfully',
        });
      } else {
        throw new Error('No reset link returned');
      }
    } catch (error) {
      console.error('Error generating reset link:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate password reset link',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!resetLink) return;

    try {
      await navigator.clipboard.writeText(resetLink);
      setCopied(true);
      toast({
        title: 'Copied',
        description: 'Reset link copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    setResetLink(null);
    setCopied(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Generate Password Reset Link
          </DialogTitle>
          <DialogDescription>
            Generate a secure password reset link for {user.name || user.email}.
            This link will expire in 24 hours and can only be used once.
          </DialogDescription>
        </DialogHeader>

        {!resetLink ? (
          <div className="py-6">
            <p className="text-sm text-muted-foreground mb-4">
              Click the button below to generate a password reset link. You can then copy
              and share this link with the user through any secure channel.
            </p>
            <Button
              onClick={generateResetLink}
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Key className="mr-2 h-4 w-4" />
                  Generate Reset Link
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Password Reset Link:
              </p>
              <div className="text-sm break-all font-mono bg-background p-3 rounded border">
                {resetLink}
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              <span>Link expires in 24 hours</span>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={copyToClipboard}
                variant="default"
                className="flex-1"
              >
                {copied ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Link
                  </>
                )}
              </Button>
            </div>

            <div className="pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                <strong>Security Note:</strong> Share this link securely with the user.
                They can use it to reset their password and regain access to their account.
                The link will become invalid after it's used once or after 24 hours.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
